import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetCostRecipe,
  getGetCostRecipeQueryKey,
  useSaveCostRecipe,
  useResetCostRecipe,
  type CostRecipeState,
} from "@workspace/api-client-react";
import { costRecipeSnapshot, type CostRecipe } from "@workspace/api-zod/cost-recipe";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, RotateCcw } from "lucide-react";
import { CostBreakdownEdit, CostBreakdownView, COST_SECTION_LABELS, type CostSection } from "./cost-breakdown-table";

export const NO_BREAKDOWN_TEXT = "No breakdown yet — this task prices from its typical rate.";

export function errorMessage(err: unknown): string {
  if (err && typeof err === "object") {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object") {
      const d = data as { message?: unknown; error?: unknown };
      if (typeof d.message === "string") return d.message;
      if (typeof d.error === "string") return d.error;
    }
    if (err instanceof Error && err.message) return err.message;
  }
  return "Something went wrong. Please try again.";
}

interface Props {
  section?: CostSection;
  taskId: string;
  source: "default" | "custom";
  unitLabel: string;
  isAdmin: boolean;
  formatCurrency: (v: string | number) => string;
}

export function CostRecipePanel({ taskId, source, unitLabel, isAdmin, formatCurrency, section }: Props) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmRestoreMaterials, setConfirmRestoreMaterials] = useState(false);
  const queryKey = getGetCostRecipeQueryKey(source, taskId);
  const { data, isLoading, isError, refetch } = useGetCostRecipe(source, taskId, { query: {
    enabled: true, queryKey, staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: true,
  } });

  function applyState(state: CostRecipeState) {
    queryClient.setQueryData(queryKey, state);
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
    if (isAdmin) queryClient.invalidateQueries({
      predicate: query => typeof query.queryKey[0] === "string" && query.queryKey[0].startsWith("/api/admin/cost-library/defaults"),
    });
  }

  const save = useSaveCostRecipe({
    mutation: {
      onSuccess: (state) => {
        applyState(state);
        setEditing(false);
        toast({ title: "Breakdown saved", description: `Typical is now ${formatCurrency(state.rateTypical)} per ${unitLabel}. Low and High have been updated proportionally.` });
      },
    },
  });
  const reset = useResetCostRecipe({
    mutation: {
      onSuccess: (state) => {
        applyState(state);
        setConfirmReset(false);
        toast({ title: "Breakdown reset to default" });
      },
      onError: (err) => {
        setConfirmReset(false);
        toast({ title: "Could not reset breakdown", description: errorMessage(err), variant: "destructive" });
      },
    },
  });
  const restoreMaterials = useSaveCostRecipe({
    mutation: {
      onSuccess: state => {
        applyState(state);
        setConfirmRestoreMaterials(false);
        toast({ title: "Original default materials restored",
          description: "The default price has been recalculated. Existing quotations and personal breakdowns are unchanged. Use Update Live to apply this to the live site." });
      },
      onError: err => toast({ title: "Could not restore materials", description: errorMessage(err), variant: "destructive" }),
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid={`panel-breakdown-loading-${taskId}`}>
        <Skeleton className="h-4 w-48" /><Skeleton className="h-4 w-full max-w-md" /><Skeleton className="h-4 w-64" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex flex-wrap items-center gap-3 text-sm" data-testid={`panel-breakdown-error-${taskId}`}>
        <span className="text-destructive">Could not load this breakdown.</span>
        <Button size="sm" variant="outline" onClick={() => refetch()} data-testid={`button-retry-breakdown-${taskId}`}>Retry</Button>
      </div>
    );
  }

  const recipe = data.recipe;

  const legacyNote = data.legacyOverride ? (
    <p className="text-xs text-muted-foreground" data-testid={`text-legacy-override-${taskId}`}>
      You have a manual rate on this task. Your manual rates and unit are kept as they are; a breakdown must add up to your current typical of {formatCurrency(data.rateTypical)}.
    </p>
  ) : null;

  if (editing) {
    return (
      <CostBreakdownEdit
        section={section}
        taskId={taskId}
        unitLabel={unitLabel}
        initial={recipe}
        sourceLabel={recipe ? (data.recipeSource === "default" ? "Default" : "Your version") : undefined}
        currentTypical={data.rateTypical}
        currentLow={data.rateLow}
        currentHigh={data.rateHigh}
        formatCurrency={formatCurrency}
        isSaving={save.isPending}
        serverError={save.isError ? errorMessage(save.error) : null}
        isFirstAdd={recipe === null}
        onCancel={() => { save.reset(); setEditing(false); }}
        onSave={(r: CostRecipe) => save.mutate({ source, id: taskId, data: { recipe: r, expectedSnapshot: costRecipeSnapshot(data) } })}
      >{legacyNote}</CostBreakdownEdit>
    );
  }

  if (!recipe) {
    return (
      <div className="space-y-3" data-testid={`panel-breakdown-${taskId}`}>
        <p className="text-sm text-muted-foreground" data-testid={`text-no-breakdown-${taskId}`}>{section ? `No ${COST_SECTION_LABELS[section].toLowerCase()} added — this task prices from its typical rate.` : NO_BREAKDOWN_TEXT}</p>
        {section && <p className="text-sm font-medium">Typical rate: {formatCurrency(data.rateTypical)} per {unitLabel}</p>}
        {legacyNote}
        <Button size="sm" variant="outline" onClick={() => { save.reset(); setEditing(true); }} data-testid={`button-add-breakdown-${taskId}`}>
          <Plus className="h-4 w-4 mr-1" /> {section ? `Add ${COST_SECTION_LABELS[section].toLowerCase()}` : "Add breakdown"}
        </Button>
      </div>
    );
  }

  const versionLabel = data.recipeSource === "override" || data.recipeSource === "custom" ? "Your version" : "Default";

  return (
    <CostBreakdownView
      section={section}
      taskId={taskId}
      unitLabel={unitLabel}
      recipe={recipe}
      rateTypical={data.rateTypical}
      sourceLabel={versionLabel}
      formatCurrency={formatCurrency}
      onEdit={() => { save.reset(); setEditing(true); }}
    >
      {legacyNote}
      {isAdmin && source === "default" && (!section || section === "materials") && (
        <div className="space-y-2 text-xs text-muted-foreground">
          <p>Admin default: saved changes apply to future quotations using this default. Use Update Live for the live site. Existing quotations and personal breakdowns stay unchanged.</p>
          <p>{(data.hasOriginalMaterials ?? !!recipe.original_materials)
            ? "The original materials are protected. Restore Default brings back their original quantities and rates, keeping current labour and plant settings."
            : "The materials shown now will be protected before your first saved admin edit."}</p>
          {(data.hasOriginalMaterials ?? !!recipe.original_materials) && (
            <Button size="sm" variant="ghost" onClick={() => setConfirmRestoreMaterials(true)}
              disabled={restoreMaterials.isPending} data-testid={`button-restore-default-materials-${taskId}`}>
              <RotateCcw className="h-4 w-4 mr-1" /> Restore Default
            </Button>
          )}
        </div>
      )}
      <AlertDialog open={confirmRestoreMaterials} onOpenChange={o => { if (!restoreMaterials.isPending) setConfirmRestoreMaterials(o); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore the original default materials?</AlertDialogTitle>
            <AlertDialogDescription>
              This replaces the current materials with the protected original list, including quantities and rates.
              Labour and plant settings stay unchanged. Low, Typical and High prices will recalculate.
              Future quotations using this default will use the restored materials; existing quotations and personal breakdowns are not changed.
              Use Update Live to apply the restored default to the live site.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreMaterials.isPending}>Keep current materials</AlertDialogCancel>
            <AlertDialogAction disabled={restoreMaterials.isPending}
              onClick={e => { e.preventDefault(); restoreMaterials.mutate({ source, id: taskId,
                data: { restoreMaterials: true, expectedSnapshot: costRecipeSnapshot(data) } }); }}
              data-testid={`button-confirm-restore-materials-${taskId}`}>
              {restoreMaterials.isPending ? "Restoring..." : "Restore Default"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {data.hasRecipeOverride && (
        <Button size="sm" variant="ghost" onClick={() => setConfirmReset(true)} disabled={reset.isPending} data-testid={`button-reset-breakdown-${taskId}`}>
          <RotateCcw className="h-4 w-4 mr-1" /> Reset to default
        </Button>
      )}
      <AlertDialog open={confirmReset} onOpenChange={o => { if (!reset.isPending) setConfirmReset(o); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to the default breakdown?</AlertDialogTitle>
            <AlertDialogDescription>
              Your version of this breakdown and its Low, Typical and High rates will be removed. The task will price from the default again. Other task preferences are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reset.isPending} data-testid={`button-cancel-reset-${taskId}`}>Keep my version</AlertDialogCancel>
            <AlertDialogAction
              disabled={reset.isPending}
              onClick={e => { e.preventDefault(); reset.mutate({ source, id: taskId }); }}
              data-testid={`button-confirm-reset-${taskId}`}
            >
              {reset.isPending ? "Resetting..." : "Reset to default"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CostBreakdownView>
  );
}
